import type { ComponentType } from 'react';
import {
  Archive,
  AppWindow,
  Bot,
  Box,
  Boxes,
  CircleHelp,
  Computer,
  CreditCard,
  Clock,
  Cloud,
  Cpu,
  Database,
  GitBranch,
  GitPullRequest,
  Globe,
  HardDrive,
  ListOrdered,
  Mail,
  MapPin,
  MessageSquare,
  Mic,
  Monitor,
  Network,
  Phone,
  Puzzle,
  Radio,
  Router,
  Server,
  ShieldCheck,
  Smartphone,
  Terminal,
  Tv,
  User,
  Wallet,
  Watch,
  Workflow,
  Zap,
  type LucideProps,
} from 'lucide-react';
import type { IconType } from 'react-icons';
import {
  SiApache,
  SiApachecassandra,
  SiApachecouchdb,
  SiApachekafka,
  SiAngular,
  SiAuth0,
  SiCircleci,
  SiClerk,
  SiCloudflare,
  SiCockroachlabs,
  SiDatadog,
  SiDigitalocean,
  SiDiscord,
  SiDjango,
  SiDocker,
  SiDotnet,
  SiDrizzle,
  SiElasticsearch,
  SiEnvoyproxy,
  SiExpress,
  SiFastapi,
  SiFirebase,
  SiFlask,
  SiFlutter,
  SiGin,
  SiGithub,
  SiGithubactions,
  SiGitlab,
  SiGooglecloud,
  SiGrafana,
  SiGraphql,
  SiHelm,
  SiHibernate,
  SiHuggingface,
  SiJenkins,
  SiKnexdotjs,
  SiKotlin,
  SiKubernetes,
  SiLaravel,
  SiMariadb,
  SiMilvus,
  SiMinio,
  SiMongodb,
  SiMongoose,
  SiMqtt,
  SiMysql,
  SiNatsdotio,
  SiNeo4J,
  SiNestjs,
  SiNetlify,
  SiNewrelic,
  SiNextdotjs,
  SiNginx,
  SiNodedotjs,
  SiNuxt,
  SiOkta,
  SiPaypal,
  SiPhoenixframework,
  SiPodman,
  SiPostgresql,
  SiPostman,
  SiPrisma,
  SiPrometheus,
  SiQdrant,
  SiRabbitmq,
  SiReact,
  SiRedis,
  SiRemix,
  SiRubyonrails,
  SiSentry,
  SiSequelize,
  SiSocketdotio,
  SiSolid,
  SiSpring,
  SiSqlalchemy,
  SiSqlite,
  SiStripe,
  SiSupabase,
  SiSvelte,
  SiSwagger,
  SiSwift,
  SiTerraform,
  SiTravisci,
  SiTrpc,
  SiTypeorm,
  SiVercel,
  SiVuedotjs,
} from 'react-icons/si';

import type { NodeCategory } from '@hyphen-db-studio/architecture-core';

export type IconRef = { set: 'si'; icon: IconType } | { set: 'lucide'; icon: ComponentType<LucideProps> };

export interface CatalogEntry {
  kind: string;
  category: NodeCategory;
  /** Optional second tree level within the category (e.g. 'SQL' / 'NoSQL' / 'Vector'
   * under Databases). Entries without one render as direct leaves of the category. */
  subCategory?: string;
  label: string;
  color: string;
  icon: IconRef;
}

function si(icon: IconType): IconRef {
  return { set: 'si', icon };
}
function lucide(icon: ComponentType<LucideProps>): IconRef {
  return { set: 'lucide', icon };
}

export const NODE_CATEGORY_LABELS: Record<NodeCategory, string> = {
  database: 'Databases',
  orm: 'ORMs / ODMs',
  api: 'APIs & Protocols',
  framework: 'Frameworks & Runtimes',
  external: 'External Services',
  infra: 'Infrastructure',
  client: 'Clients',
  generic: 'Generic',
};

export const NODE_CATALOG: CatalogEntry[] = [
  // Databases
  { kind: 'postgres', category: 'database', subCategory: 'SQL', label: 'PostgreSQL', color: '#336791', icon: si(SiPostgresql) },
  { kind: 'mysql', category: 'database', subCategory: 'SQL', label: 'MySQL', color: '#4479A1', icon: si(SiMysql) },
  { kind: 'mariadb', category: 'database', subCategory: 'SQL', label: 'MariaDB', color: '#003545', icon: si(SiMariadb) },
  { kind: 'sqlite', category: 'database', subCategory: 'SQL', label: 'SQLite', color: '#003B57', icon: si(SiSqlite) },
  { kind: 'cockroachdb', category: 'database', subCategory: 'SQL', label: 'CockroachDB', color: '#6933FF', icon: si(SiCockroachlabs) },
  { kind: 'mongodb', category: 'database', subCategory: 'NoSQL', label: 'MongoDB', color: '#47A248', icon: si(SiMongodb) },
  { kind: 'couchdb', category: 'database', subCategory: 'NoSQL', label: 'CouchDB', color: '#E42528', icon: si(SiApachecouchdb) },
  { kind: 'cassandra', category: 'database', subCategory: 'NoSQL', label: 'Cassandra', color: '#1287B1', icon: si(SiApachecassandra) },
  { kind: 'redis', category: 'database', subCategory: 'Cache', label: 'Redis', color: '#DC382D', icon: si(SiRedis) },
  { kind: 'neo4j', category: 'database', subCategory: 'Graph', label: 'Neo4j', color: '#4581C3', icon: si(SiNeo4J) },
  { kind: 'elasticsearch', category: 'database', subCategory: 'Search', label: 'Elasticsearch', color: '#005571', icon: si(SiElasticsearch) },
  { kind: 'pinecone', category: 'database', subCategory: 'Vector', label: 'Pinecone', color: '#1C17FF', icon: lucide(Database) },
  { kind: 'weaviate', category: 'database', subCategory: 'Vector', label: 'Weaviate', color: '#66CC66', icon: lucide(Database) },
  { kind: 'chroma', category: 'database', subCategory: 'Vector', label: 'Chroma', color: '#FFC107', icon: lucide(Database) },
  { kind: 'milvus', category: 'database', subCategory: 'Vector', label: 'Milvus', color: '#00A1EA', icon: si(SiMilvus) },
  { kind: 'qdrant', category: 'database', subCategory: 'Vector', label: 'Qdrant', color: '#DC244C', icon: si(SiQdrant) },

  // ORMs / ODMs
  { kind: 'prisma', category: 'orm', subCategory: 'SQL ORM', label: 'Prisma', color: '#2D3748', icon: si(SiPrisma) },
  { kind: 'sequelize', category: 'orm', subCategory: 'SQL ORM', label: 'Sequelize', color: '#52B0E7', icon: si(SiSequelize) },
  { kind: 'typeorm', category: 'orm', subCategory: 'SQL ORM', label: 'TypeORM', color: '#E83524', icon: si(SiTypeorm) },
  { kind: 'sqlalchemy', category: 'orm', subCategory: 'SQL ORM', label: 'SQLAlchemy', color: '#D71F00', icon: si(SiSqlalchemy) },
  { kind: 'drizzle', category: 'orm', subCategory: 'SQL ORM', label: 'Drizzle', color: '#C5F74F', icon: si(SiDrizzle) },
  { kind: 'knex', category: 'orm', subCategory: 'SQL ORM', label: 'Knex.js', color: '#D26B38', icon: si(SiKnexdotjs) },
  { kind: 'hibernate', category: 'orm', subCategory: 'SQL ORM', label: 'Hibernate', color: '#59666C', icon: si(SiHibernate) },
  { kind: 'mongoose', category: 'orm', subCategory: 'ODM', label: 'Mongoose', color: '#880000', icon: si(SiMongoose) },

  // APIs & Protocols
  { kind: 'graphql', category: 'api', subCategory: 'Query & RPC', label: 'GraphQL', color: '#E10098', icon: si(SiGraphql) },
  { kind: 'grpc', category: 'api', subCategory: 'Query & RPC', label: 'gRPC', color: '#4285F4', icon: lucide(Network) },
  { kind: 'trpc', category: 'api', subCategory: 'Query & RPC', label: 'tRPC', color: '#2596BE', icon: si(SiTrpc) },
  { kind: 'websocket', category: 'api', subCategory: 'Real-time', label: 'WebSocket', color: '#f59e0b', icon: lucide(Radio) },
  { kind: 'socketio', category: 'api', subCategory: 'Real-time', label: 'Socket.IO', color: '#010101', icon: si(SiSocketdotio) },
  { kind: 'mqtt', category: 'api', subCategory: 'Real-time', label: 'MQTT', color: '#660066', icon: si(SiMqtt) },
  { kind: 'webhook', category: 'api', subCategory: 'Real-time', label: 'Webhook', color: '#8b5cf6', icon: lucide(Zap) },
  { kind: 'rest-api', category: 'api', subCategory: 'REST & Docs', label: 'REST API', color: '#0ea5e9', icon: lucide(Globe) },
  { kind: 'openapi', category: 'api', subCategory: 'REST & Docs', label: 'OpenAPI / Swagger', color: '#85EA2D', icon: si(SiSwagger) },
  { kind: 'postman', category: 'api', subCategory: 'REST & Docs', label: 'Postman', color: '#FF6C37', icon: si(SiPostman) },

  // Frameworks & runtimes
  { kind: 'react', category: 'framework', subCategory: 'Frontend', label: 'React', color: '#61DAFB', icon: si(SiReact) },
  { kind: 'vue', category: 'framework', subCategory: 'Frontend', label: 'Vue', color: '#4FC08D', icon: si(SiVuedotjs) },
  { kind: 'angular', category: 'framework', subCategory: 'Frontend', label: 'Angular', color: '#DD0031', icon: si(SiAngular) },
  { kind: 'svelte', category: 'framework', subCategory: 'Frontend', label: 'Svelte', color: '#FF3E00', icon: si(SiSvelte) },
  { kind: 'nextjs', category: 'framework', subCategory: 'Frontend', label: 'Next.js', color: '#000000', icon: si(SiNextdotjs) },
  { kind: 'nuxt', category: 'framework', subCategory: 'Frontend', label: 'Nuxt', color: '#00DC82', icon: si(SiNuxt) },
  { kind: 'remix', category: 'framework', subCategory: 'Frontend', label: 'Remix', color: '#000000', icon: si(SiRemix) },
  { kind: 'solidjs', category: 'framework', subCategory: 'Frontend', label: 'SolidJS', color: '#2C4F7C', icon: si(SiSolid) },
  { kind: 'nodejs', category: 'framework', subCategory: 'Backend', label: 'Node.js', color: '#339933', icon: si(SiNodedotjs) },
  { kind: 'express', category: 'framework', subCategory: 'Backend', label: 'Express', color: '#000000', icon: si(SiExpress) },
  { kind: 'nestjs', category: 'framework', subCategory: 'Backend', label: 'NestJS', color: '#E0234E', icon: si(SiNestjs) },
  { kind: 'django', category: 'framework', subCategory: 'Backend', label: 'Django', color: '#092E20', icon: si(SiDjango) },
  { kind: 'flask', category: 'framework', subCategory: 'Backend', label: 'Flask', color: '#000000', icon: si(SiFlask) },
  { kind: 'fastapi', category: 'framework', subCategory: 'Backend', label: 'FastAPI', color: '#009688', icon: si(SiFastapi) },
  { kind: 'spring', category: 'framework', subCategory: 'Backend', label: 'Spring', color: '#6DB33F', icon: si(SiSpring) },
  { kind: 'laravel', category: 'framework', subCategory: 'Backend', label: 'Laravel', color: '#FF2D20', icon: si(SiLaravel) },
  { kind: 'rails', category: 'framework', subCategory: 'Backend', label: 'Ruby on Rails', color: '#CC0000', icon: si(SiRubyonrails) },
  { kind: 'gin', category: 'framework', subCategory: 'Backend', label: 'Gin (Go)', color: '#00ADD8', icon: si(SiGin) },
  { kind: 'dotnet', category: 'framework', subCategory: 'Backend', label: 'ASP.NET', color: '#512BD4', icon: si(SiDotnet) },
  { kind: 'phoenix', category: 'framework', subCategory: 'Backend', label: 'Phoenix (Elixir)', color: '#FD4F00', icon: si(SiPhoenixframework) },
  { kind: 'flutter', category: 'framework', subCategory: 'Mobile', label: 'Flutter', color: '#02569B', icon: si(SiFlutter) },
  { kind: 'react-native', category: 'framework', subCategory: 'Mobile', label: 'React Native', color: '#61DAFB', icon: si(SiReact) },
  { kind: 'swift', category: 'framework', subCategory: 'Mobile', label: 'Swift (iOS)', color: '#F05138', icon: si(SiSwift) },
  { kind: 'kotlin', category: 'framework', subCategory: 'Mobile', label: 'Kotlin (Android)', color: '#7F52FF', icon: si(SiKotlin) },

  // External services
  { kind: 'github', category: 'external', subCategory: 'Source Control', label: 'GitHub', color: '#181717', icon: si(SiGithub) },
  { kind: 'gitlab', category: 'external', subCategory: 'Source Control', label: 'GitLab', color: '#FC6D26', icon: si(SiGitlab) },
  { kind: 'git-branch', category: 'external', subCategory: 'Source Control', label: 'Git Branch', color: '#F05032', icon: lucide(GitBranch) },
  { kind: 'merge-request', category: 'external', subCategory: 'Source Control', label: 'Merge / Pull Request', color: '#FC6D26', icon: lucide(GitPullRequest) },
  { kind: 'ci-pipeline', category: 'external', subCategory: 'CI/CD', label: 'CI/CD Pipeline', color: '#FC6D26', icon: lucide(Workflow) },
  { kind: 'github-actions', category: 'external', subCategory: 'CI/CD', label: 'GitHub Actions', color: '#2088FF', icon: si(SiGithubactions) },
  { kind: 'jenkins', category: 'external', subCategory: 'CI/CD', label: 'Jenkins', color: '#D24939', icon: si(SiJenkins) },
  { kind: 'circleci', category: 'external', subCategory: 'CI/CD', label: 'CircleCI', color: '#343434', icon: si(SiCircleci) },
  { kind: 'travisci', category: 'external', subCategory: 'CI/CD', label: 'Travis CI', color: '#3EAAAF', icon: si(SiTravisci) },
  { kind: 'docker', category: 'external', subCategory: 'Containers & IaC', label: 'Docker', color: '#2496ED', icon: si(SiDocker) },
  { kind: 'kubernetes', category: 'external', subCategory: 'Containers & IaC', label: 'Kubernetes', color: '#326CE5', icon: si(SiKubernetes) },
  { kind: 'terraform', category: 'external', subCategory: 'Containers & IaC', label: 'Terraform', color: '#7B42BC', icon: si(SiTerraform) },
  { kind: 'helm', category: 'external', subCategory: 'Containers & IaC', label: 'Helm', color: '#0F1689', icon: si(SiHelm) },
  { kind: 'podman', category: 'external', subCategory: 'Containers & IaC', label: 'Podman', color: '#892CA0', icon: si(SiPodman) },
  { kind: 'aws', category: 'external', subCategory: 'Cloud & Hosting', label: 'AWS', color: '#FF9900', icon: lucide(Cloud) },
  { kind: 'aws-s3', category: 'external', subCategory: 'Cloud & Hosting', label: 'AWS S3', color: '#FF9900', icon: lucide(HardDrive) },
  { kind: 'aws-lambda', category: 'external', subCategory: 'Cloud & Hosting', label: 'AWS Lambda', color: '#FF9900', icon: lucide(Zap) },
  { kind: 'googlecloud', category: 'external', subCategory: 'Cloud & Hosting', label: 'Google Cloud', color: '#4285F4', icon: si(SiGooglecloud) },
  { kind: 'azure', category: 'external', subCategory: 'Cloud & Hosting', label: 'Azure', color: '#0078D4', icon: lucide(Cloud) },
  { kind: 'digitalocean', category: 'external', subCategory: 'Cloud & Hosting', label: 'DigitalOcean', color: '#0080FF', icon: si(SiDigitalocean) },
  { kind: 'heroku', category: 'external', subCategory: 'Cloud & Hosting', label: 'Heroku', color: '#430098', icon: lucide(Cloud) },
  { kind: 'vercel', category: 'external', subCategory: 'Cloud & Hosting', label: 'Vercel', color: '#000000', icon: si(SiVercel) },
  { kind: 'netlify', category: 'external', subCategory: 'Cloud & Hosting', label: 'Netlify', color: '#00C7B7', icon: si(SiNetlify) },
  { kind: 'cloudflare', category: 'external', subCategory: 'Cloud & Hosting', label: 'Cloudflare', color: '#F38020', icon: si(SiCloudflare) },
  { kind: 'firebase', category: 'external', subCategory: 'Cloud & Hosting', label: 'Firebase', color: '#FFCA28', icon: si(SiFirebase) },
  { kind: 'supabase', category: 'external', subCategory: 'Cloud & Hosting', label: 'Supabase', color: '#3FCF8E', icon: si(SiSupabase) },
  { kind: 'stripe', category: 'external', subCategory: 'Payments', label: 'Stripe', color: '#635BFF', icon: si(SiStripe) },
  { kind: 'paypal', category: 'external', subCategory: 'Payments', label: 'PayPal', color: '#00457C', icon: si(SiPaypal) },
  { kind: 'flutterwave', category: 'external', subCategory: 'Payments', label: 'Flutterwave', color: '#F5A623', icon: lucide(CreditCard) },
  { kind: 'mobile-money', category: 'external', subCategory: 'Payments', label: 'Mobile Money', color: '#10b981', icon: lucide(Wallet) },
  { kind: 'auth0', category: 'external', subCategory: 'Auth & Identity', label: 'Auth0', color: '#EB5424', icon: si(SiAuth0) },
  { kind: 'okta', category: 'external', subCategory: 'Auth & Identity', label: 'Okta', color: '#007DC1', icon: si(SiOkta) },
  { kind: 'clerk', category: 'external', subCategory: 'Auth & Identity', label: 'Clerk', color: '#6C47FF', icon: si(SiClerk) },
  { kind: 'sentry', category: 'external', subCategory: 'Observability', label: 'Sentry', color: '#362D59', icon: si(SiSentry) },
  { kind: 'datadog', category: 'external', subCategory: 'Observability', label: 'Datadog', color: '#632CA6', icon: si(SiDatadog) },
  { kind: 'grafana', category: 'external', subCategory: 'Observability', label: 'Grafana', color: '#F46800', icon: si(SiGrafana) },
  { kind: 'prometheus', category: 'external', subCategory: 'Observability', label: 'Prometheus', color: '#E6522C', icon: si(SiPrometheus) },
  { kind: 'newrelic', category: 'external', subCategory: 'Observability', label: 'New Relic', color: '#008C99', icon: si(SiNewrelic) },
  { kind: 'slack', category: 'external', subCategory: 'Communication & AI', label: 'Slack', color: '#4A154B', icon: lucide(MessageSquare) },
  { kind: 'twilio', category: 'external', subCategory: 'Communication & AI', label: 'Twilio', color: '#F22F46', icon: lucide(MessageSquare) },
  { kind: 'africastalking', category: 'external', subCategory: 'Communication & AI', label: "Africa's Talking", color: '#F37021', icon: lucide(Radio) },
  { kind: 'email-smtp', category: 'external', subCategory: 'Communication & AI', label: 'Email / SMTP', color: '#0ea5e9', icon: lucide(Mail) },
  { kind: 'gps-tracking', category: 'external', subCategory: 'Communication & AI', label: 'GPS Tracking', color: '#14b8a6', icon: lucide(MapPin) },
  { kind: 'discord', category: 'external', subCategory: 'Communication & AI', label: 'Discord', color: '#5865F2', icon: si(SiDiscord) },
  { kind: 'openai', category: 'external', subCategory: 'Communication & AI', label: 'OpenAI / LLM API', color: '#412991', icon: lucide(Bot) },
  { kind: 'huggingface', category: 'external', subCategory: 'Communication & AI', label: 'Hugging Face', color: '#FFD21E', icon: si(SiHuggingface) },

  // Infrastructure
  { kind: 'kafka', category: 'infra', subCategory: 'Messaging', label: 'Kafka', color: '#231F20', icon: si(SiApachekafka) },
  { kind: 'rabbitmq', category: 'infra', subCategory: 'Messaging', label: 'RabbitMQ', color: '#FF6600', icon: si(SiRabbitmq) },
  { kind: 'nats', category: 'infra', subCategory: 'Messaging', label: 'NATS', color: '#27AAE1', icon: si(SiNatsdotio) },
  { kind: 'message-queue', category: 'infra', subCategory: 'Messaging', label: 'Message Queue', color: '#8b5cf6', icon: lucide(ListOrdered) },
  { kind: 'nginx', category: 'infra', subCategory: 'Web Server & Proxy', label: 'Nginx', color: '#009639', icon: si(SiNginx) },
  { kind: 'apache', category: 'infra', subCategory: 'Web Server & Proxy', label: 'Apache', color: '#D22128', icon: si(SiApache) },
  { kind: 'envoy', category: 'infra', subCategory: 'Web Server & Proxy', label: 'Envoy Proxy', color: '#AC6199', icon: si(SiEnvoyproxy) },
  { kind: 'load-balancer', category: 'infra', subCategory: 'Web Server & Proxy', label: 'Load Balancer', color: '#10b981', icon: lucide(GitBranch) },
  { kind: 'cdn', category: 'infra', subCategory: 'Web Server & Proxy', label: 'CDN', color: '#0ea5e9', icon: lucide(Globe) },
  { kind: 'cache', category: 'infra', subCategory: 'Storage & Cache', label: 'Cache', color: '#f59e0b', icon: lucide(Zap) },
  { kind: 'blob-storage', category: 'infra', subCategory: 'Storage & Cache', label: 'Blob Storage', color: '#64748b', icon: lucide(HardDrive) },
  { kind: 'minio', category: 'infra', subCategory: 'Storage & Cache', label: 'MinIO', color: '#C72E49', icon: si(SiMinio) },
  { kind: 'cron-job', category: 'infra', subCategory: 'Jobs', label: 'Cron Job', color: '#64748b', icon: lucide(Clock) },
  { kind: 'auth-service', category: 'infra', subCategory: 'Security & Gateway', label: 'Auth / Identity', color: '#ef4444', icon: lucide(ShieldCheck) },
  { kind: 'api-gateway', category: 'infra', subCategory: 'Security & Gateway', label: 'API Gateway', color: '#ef4444', icon: lucide(Router) },

  // Clients
  { kind: 'browser', category: 'client', subCategory: 'Web', label: 'Browser', color: '#6366f1', icon: lucide(Monitor) },
  { kind: 'phone', category: 'client', subCategory: 'Mobile', label: 'Phone', color: '#6366f1', icon: lucide(Smartphone) },
  { kind: 'mobile-app', category: 'client', subCategory: 'Mobile', label: 'Mobile App', color: '#6366f1', icon: lucide(Smartphone) },
  { kind: 'ussd', category: 'client', subCategory: 'Mobile', label: 'USSD', color: '#6366f1', icon: lucide(Phone) },
  { kind: 'pc', category: 'client', subCategory: 'Desktop & CLI', label: 'PC', color: '#6366f1', icon: lucide(Computer) },
  { kind: 'desktop-app', category: 'client', subCategory: 'Desktop & CLI', label: 'Desktop App', color: '#6366f1', icon: lucide(AppWindow) },
  { kind: 'terminal', category: 'client', subCategory: 'Desktop & CLI', label: 'Terminal / CLI', color: '#6366f1', icon: lucide(Terminal) },
  { kind: 'smart-tv', category: 'client', subCategory: 'Embedded', label: 'Smart TV', color: '#6366f1', icon: lucide(Tv) },
  { kind: 'iot-device', category: 'client', subCategory: 'Embedded', label: 'IoT Device', color: '#6366f1', icon: lucide(Cpu) },
  { kind: 'wearable', category: 'client', subCategory: 'Embedded', label: 'Wearable', color: '#6366f1', icon: lucide(Watch) },
  { kind: 'voice-assistant', category: 'client', subCategory: 'Embedded', label: 'Voice Assistant', color: '#6366f1', icon: lucide(Mic) },

  // Generic
  { kind: 'actor', category: 'generic', subCategory: 'People & Boundaries', label: 'User / Actor', color: '#94a3b8', icon: lucide(User) },
  { kind: 'group-boundary', category: 'generic', subCategory: 'People & Boundaries', label: 'Group / Boundary', color: '#94a3b8', icon: lucide(Boxes) },
  { kind: 'service', category: 'generic', subCategory: 'Fallbacks', label: 'Service', color: '#94a3b8', icon: lucide(Box) },
  { kind: 'server', category: 'generic', subCategory: 'Fallbacks', label: 'Server', color: '#94a3b8', icon: lucide(Server) },
  { kind: 'third-party', category: 'generic', subCategory: 'Fallbacks', label: 'Third-Party Service', color: '#94a3b8', icon: lucide(Puzzle) },
  { kind: 'legacy-system', category: 'generic', subCategory: 'Fallbacks', label: 'Legacy System', color: '#94a3b8', icon: lucide(Archive) },
  { kind: 'unknown', category: 'generic', subCategory: 'Fallbacks', label: 'Unknown / TBD', color: '#94a3b8', icon: lucide(CircleHelp) },
];

const CATALOG_BY_KIND = new Map(NODE_CATALOG.map((entry) => [entry.kind, entry]));

export function getCatalogEntry(kind: string): CatalogEntry | undefined {
  return CATALOG_BY_KIND.get(kind);
}

export const NODE_CATEGORIES: NodeCategory[] = [
  'database',
  'orm',
  'api',
  'framework',
  'external',
  'infra',
  'client',
  'generic',
];

export function catalogByCategory(category: NodeCategory): CatalogEntry[] {
  return NODE_CATALOG.filter((entry) => entry.category === category);
}
